'use client';

/**
 * LandingFeaturedCoupons — landing section showing top 3 active coupons.
 *
 * PR #1444 (v124.2): Founder directive — "agreguemos los cupones con
 * acceso desde la landing principal para que sea más visible".
 *
 * Fetches `GET /api/market/coupons/featured?limit=3` (PUBLIC endpoint
 * added in this PR). Renders a 3-column grid of `<CouponCardMini>`.
 *
 * UX:
 *   - Heading: "Cupones disponibles"
 *   - Subtitle: brief copy motivating click
 *   - Grid: 1 col mobile, 2 col tablet, 3 col desktop
 *   - CTA button: "Ver todos los cupones" → `/market/cupones`
 *
 * Empty state: returns null (don't render section if 0 active coupons).
 * Loading state: skeleton loader (3 cards shimmer).
 * Error state: silent fail + return null.
 *
 * Anti-P-043 (AbortController): in-flight fetch cancels on unmount.
 *
 * Spec ref: PR #1444 + SPEC-242 Track C #15 (Cuponera), SPEC-NEGOCIO
 *           §10.13 (Cuponera Externa).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CouponCardMini, type FeaturedCoupon } from './coupon-card-mini';
import { apiFetch, ApiClientError } from '@/lib/api-client';


export function LandingFeaturedCoupons() {
  const [coupons, setCoupons] = useState<FeaturedCoupon[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchFeatured = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // NOTE: apiFetch auto-unwraps `.data` by default (see fetch-client.ts).
      // So `res` IS the array, NOT `{ data: array }`. Don't access res.data.
      const res = await apiFetch<FeaturedCoupon[]>(
        '/market/coupons/featured?limit=3',
        { signal: controller.signal },
      );
      setCoupons(res ?? []);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 0) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setCoupons([]);
    }
  }, []);

  useEffect(() => {
    void fetchFeatured();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchFeatured]);

  if (coupons === null) {
    return (
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              Cupones disponibles
            </h2>
            <p className="text-muted-foreground">Cargando cupones…</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-32 rounded-2xl bg-card animate-pulse border border-border"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (coupons.length === 0) return null;

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">
            <Ticket className="size-4" />
            <span className="text-sm font-medium">Cupones</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            Cupones disponibles
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Ofertas exclusivas de comercios aliados de Profileia. Redímelas
            con tus créditos.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {coupons.map((coupon) => (
            <CouponCardMini key={coupon._id} coupon={coupon} />
          ))}
        </div>

        <div className="text-center">
          <Button asChild size="lg" variant="outline">
            <Link href="/market/cupones">
              Ver todos los cupones
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
